import axios from 'axios';
const WSSP_API_URL = process.env.REACT_APP_ENV === 'production'
  ? process.env.REACT_APP_WSSP_API_URL_PRODUCTION
  : process.env.REACT_APP_WSSP_API_URL;

const WSSP_SOCKET_URL = process.env.REACT_APP_ENV === 'production'
  ? process.env.REACT_APP_WSSP_SOCKET_URL_PRODUCTION
  : process.env.REACT_APP_WSSP_SOCKET_URL;

const baseConfig = {
  baseURL: WSSP_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
};

const standardTimeout = 30000; // 30 segundos
const mediaTimeout = 120000; // 2 minutos para multimedia

// =================== INSTANCIA PÚBLICA (SIN AUTENTICACIÓN) ===================
export const wsspPublicApi = axios.create({
  ...baseConfig,
  timeout: standardTimeout,
});

// =================== FACTORY PARA API AUTENTICADA DEL MICROSERVICIO ===================
export const createWsspAuthenticatedApi = (token) => {
  const wsspAuthenticatedApi = axios.create({
    ...baseConfig,
    timeout: standardTimeout,
    headers: {
      ...baseConfig.headers,
      Authorization: token ? `Bearer ${token}` : '',
    },
  });

  // Interceptor para manejo de errores y renovación de tokens
  wsspAuthenticatedApi.interceptors.response.use(
    (response) => {
      // Verificar si hay un nuevo token en las headers
      const newToken = response.headers['x-new-token'];
      const tokenRenewed = response.headers['x-token-renewed'];

      if (newToken && tokenRenewed === 'true') {
        console.log('🔄 WSSP: Token renovado automáticamente');
        // Guardar el nuevo token
        localStorage.setItem('wssp_token', newToken);
        // Emitir evento personalizado para que otros componentes se enteren
        window.dispatchEvent(
          new CustomEvent('wssp:token-renewed', { detail: { token: newToken } })
        );
      }

      return response;
    },
    async (error) => {
      if (error.response?.status === 401) {
        console.log('❌ WSSP: Token expirado o inválido');
        // Limpiar token y redirigir al login del microservicio
        localStorage.removeItem('wssp_token');
        window.dispatchEvent(new CustomEvent('wssp:auth-required'));
      }

      // Manejo específico de timeouts
      if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
        console.error('⏱️ WSSP: Timeout en la petición');
        error.message = 'La petición al microservicio tardó demasiado tiempo';
      }

      return Promise.reject(error);
    }
  );

  return wsspAuthenticatedApi;
};

// =================== FACTORY PARA API DE MULTIMEDIA CON TIMEOUT EXTENDIDO ===================
export const createWsspMediaApi = (token) => {
  const wsspMediaApi = axios.create({
    ...baseConfig,
    timeout: mediaTimeout,
    headers: {
      ...baseConfig.headers,
      Authorization: token ? `Bearer ${token}` : '',
    },
  });

  wsspMediaApi.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (error.response?.status === 401) {
        localStorage.removeItem('wssp_token');
        window.dispatchEvent(new CustomEvent('wssp:auth-required'));
      }

      if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
        console.error('⏱️ WSSP Media: Timeout descargando multimedia');
        error.message = 'La descarga del archivo tardó demasiado tiempo';
      }

      return Promise.reject(error);
    }
  );

  return wsspMediaApi;
};

// =================== EXPORTAR URL DEL SOCKET ===================
export const getWsspSocketUrl = () => WSSP_SOCKET_URL;

// =================== SERVICIO DE AUTENTICACIÓN OTP ===================
export const WsspAuthService = {
  // Solicitar código OTP
  requestOTP: async (phoneNumber) => {
    try {
      const response = await wsspPublicApi.post('/api/auth/request-otp', {
        phoneNumber,
      });
      return response.data;
    } catch (error) {
      console.error('Error solicitando OTP:', error);
      throw error;
    }
  },

  // Verificar código OTP y obtener token
  verifyOTP: async (phoneNumber, otp) => {
    try {
      const response = await wsspPublicApi.post('/api/auth/verify-otp', {
        phoneNumber,
        otp,
      });
      
      if (response.data.success && response.data.token) {
        // Guardar token en localStorage
        localStorage.setItem('wssp_token', response.data.token);
        localStorage.setItem('wssp_phone', phoneNumber);
      }
      
      return response.data;
    } catch (error) {
      console.error('Error verificando OTP:', error);
      throw error;
    }
  },

  // Validar token actual
  validateToken: async (token) => {
    try {
      const api = createWsspAuthenticatedApi(token);
      const response = await api.get('/api/auth/validate');
      return response.data;
    } catch (error) {
      console.error('Error validando token:', error);
      return { success: false };
    }
  },

  // Cerrar sesión
  logout: async (token) => {
    try {
      const api = createWsspAuthenticatedApi(token);
      await api.post('/api/auth/logout');
      
      // Limpiar localStorage
      localStorage.removeItem('wssp_token');
      localStorage.removeItem('wssp_phone');
      
      return { success: true };
    } catch (error) {
      console.error('Error cerrando sesión:', error);
      throw error;
    }
  },

  // Obtener token almacenado
  getStoredToken: () => {
    return localStorage.getItem('wssp_token');
  },

  // Verificar si hay sesión activa
  hasActiveSession: () => {
    return !!localStorage.getItem('wssp_token');
  },
};

// =================== SERVICIO DE WHATSAPP ===================
export const WsspWhatsAppService = {
  // Obtener estado de WhatsApp
  getStatus: async (token) => {
    try {
      const api = createWsspAuthenticatedApi(token);
      const response = await api.get('/api/whatsapp/status');
      return response.data;
    } catch (error) {
      console.error('Error obteniendo estado de WhatsApp:', error);
      throw error;
    }
  },

  // Obtener código QR
  getQR: async (token) => {
    try {
      const api = createWsspAuthenticatedApi(token);
      const response = await api.get('/api/whatsapp/qr');
      return response.data;
    } catch (error) {
      console.error('Error obteniendo QR:', error);
      throw error;
    }
  },

  // Obtener chats
  getChats: async (token) => {
    try {
      const api = createWsspAuthenticatedApi(token);
      const response = await api.get('/api/whatsapp/chats');
      return response.data;
    } catch (error) {
      console.error('Error obteniendo chats:', error);
      throw error;
    }
  },

  // Enviar mensaje
  sendMessage: async (token, number, message, mediaPath = null) => {
    try {
      const api = createWsspAuthenticatedApi(token);
      const response = await api.post('/api/whatsapp/send-message', {
        number,
        message,
        mediaPath,
      });
      return response.data;
    } catch (error) {
      console.error('Error enviando mensaje:', error);
      throw error;
    }
  },

  // Descargar multimedia
  downloadMedia: async (token, messageId) => {
    try {
      const api = createWsspMediaApi(token);
      const response = await api.get(`/api/whatsapp/download-media/${messageId}`);
      return response.data;
    } catch (error) {
      console.error('Error descargando multimedia:', error);
      throw error;
    }
  },
};

const wsspApiServices = {
  WsspAuthService,
  WsspWhatsAppService,
  getWsspSocketUrl,
};

export default wsspApiServices;
